import {
  JGY_EMBEDDED_AT_ROOT,
  JGY_STATIC_TENANT_ID,
} from "@/constants/jgyEmbedded";
import { getTenantId, setLocalTenantId } from "@/lib/storeManager";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

interface TenantContextType {
  tenantId: string | null;
  setTenantId: (id: string) => void;
  clearTenantId: () => void;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error("useTenant must be used within a TenantProvider");
  }
  return context;
};

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [tenantId, setTenantIdState] = useState<string | null>(() => {
    if (JGY_EMBEDDED_AT_ROOT) {
      return JGY_STATIC_TENANT_ID;
    }
    const storedTenantId = getTenantId();
    if (storedTenantId) {
      return storedTenantId;
    }
    const pathParts = window.location.pathname.split("/").filter(Boolean);
    return pathParts.length > 0 ? pathParts[0] : null;
  });

  const setTenantIdHandler = (id: string) => {
    setLocalTenantId(id);
    setTenantIdState(id);
  };

  const clearTenantId = () => {
    localStorage.removeItem("tenantId");
    setTenantIdState(null);
  };

  useEffect(() => {
    if (JGY_EMBEDDED_AT_ROOT) {
      setLocalTenantId(JGY_STATIC_TENANT_ID);
      setTenantIdState(JGY_STATIC_TENANT_ID);
      return;
    }

    const handleUrlChange = () => {
      const pathParts = window.location.pathname.split("/").filter(Boolean);
      const urlTenantId = pathParts.length > 0 ? pathParts[0] : null;

      if (
        urlTenantId &&
        urlTenantId !== "undefined" &&
        urlTenantId !== "null"
      ) {
        setTenantIdHandler(urlTenantId);
      }
    };

    window.addEventListener("popstate", handleUrlChange);
    handleUrlChange();

    return () => {
      window.removeEventListener("popstate", handleUrlChange);
    };
  }, []);

  return (
    <TenantContext.Provider
      value={{
        tenantId,
        setTenantId: setTenantIdHandler,
        clearTenantId,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

// Export the context for use in other files
export { TenantContext };
